/** Voter migration application (Form-13, rule 26(7)) — data helpers. */

export const INITIAL_VOTER_MIGRATION_FORM = {
    // প্রাপক block
    officeUpazila: '',
    officeDistrict: '',
    // ১-৩
    applicantName: '',
    nid: '',
    birthDate: '',
    // ৪ — current enrolment
    currentVoterNo: '',
    currentAreaName: '',
    currentAreaNo: '',
    currentUpazila: '',
    currentDistrict: '',
    currentVillage: '',
    currentHolding: '',
    // ৫ — destination area
    newDistrict: '',
    newUpazila: '',
    newLocalBody: '',
    newWardNo: '',
    newAreaName: '',
    newAreaNo: '',
    newVillage: '',
    newHolding: '',
    phone: '',
    postOffice: '',
    postCode: '',
    // ৬-৭
    stayingSince: '',
    migrationReason: '',
    // সনাক্তকারী (optional)
    identifierName: '',
    identifierNid: '',
    identifierAddress: '',
};

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

/** Convert Bengali/Arabic-Indic digits to ASCII — used only for validation logic. */
function toAsciiDigits(str) {
    return str
        .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

const BENGALI_DIGITS = '০১২৩৪৫৬৭৮৯';

/** Form-13 is printed in Bengali, so its numbers are written in Bengali too. */
export function toBengaliDigits(value) {
    return String(value ?? '').replace(/\d/g, (digit) => BENGALI_DIGITS[Number(digit)]);
}

/**
 * A date as the form writes it: দিন-মাস-বছর in Bengali numerals, the shape the
 * printed line and the field's own placeholder use.
 * @param {Date | null} date
 */
export function formatDateField(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return toBengaliDigits(`${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`);
}

/**
 * Read a stored date back for the picker. Anything the field could already
 * hold is accepted — Bengali or ASCII digits, and -, / or . as the separator —
 * so a snapshot typed before the picker existed still opens on its own date.
 * @returns {Date | null}
 */
export function parseDateField(value) {
    const match = toAsciiDigits(String(value ?? '').trim())
        .match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    // Reject a date the calendar rolled over, e.g. 31-02-2020.
    if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
    return date;
}

/** Keep only digit characters (ASCII 0-9, Bengali ০-৯, Arabic-Indic ٠-٩). */
export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

/** Strip to digits, drop leading 880 country code if present. */
export function normalizeBdPhone(value) {
    let digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) {
        digits = digits.slice(3);
    }
    return digits.slice(0, 11);
}

/**
 * The form takes a telephone *or* mobile number, so the only rule is the one
 * every Bangladeshi number shares: 11 digits starting with 0.
 */
export function isValidBdPhone(value) {
    const ascii = toAsciiDigits(normalizeBdPhone(value));
    return /^0\d{10}$/.test(ascii);
}

const FORM_SNAPSHOT_KEY = 'voter-migration-form-last-snapshot';

/** Everything the office needs; the সনাক্তকারী block stays optional. */
const REQUIRED_FIELDS = [
    'officeUpazila',
    'officeDistrict',
    'applicantName',
    'nid',
    'birthDate',
    'currentVoterNo',
    'currentAreaName',
    'currentUpazila',
    'currentDistrict',
    'currentVillage',
    'newDistrict',
    'newUpazila',
    'newLocalBody',
    'newAreaName',
    'newVillage',
    'phone',
    'stayingSince',
    'migrationReason',
];

/** @returns {typeof INITIAL_VOTER_MIGRATION_FORM | null} */
export function getVoterMigrationFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...INITIAL_VOTER_MIGRATION_FORM, ...parsed };
    } catch {
        return null;
    }
}

/** Save the latest application so it can be restored later. */
export function saveVoterMigrationFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_VOTER_MIGRATION_FORM)) {
            payload[key] = displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdPhone(value);
    if (!digits) {
        return '';
    }
    if (digits.length < 11) {
        return t('voterMigrationForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdPhone(digits)) {
        return t('voterMigrationForm.validation.mobileInvalid');
    }
    return '';
}

/**
 * @param {typeof INITIAL_VOTER_MIGRATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ field: string, message: string }[]}
 */
export function getVoterMigrationFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (key === 'phone') continue;
        if (!displayField(form[key])) {
            errors.push({
                field: key,
                message: t('voterMigrationForm.validation.required', {
                    field: t(`voterMigrationForm.fields.${key}`),
                }),
            });
        }
    }

    const phone = displayField(form.phone);
    if (!phone) {
        errors.push({ field: 'phone', message: t('voterMigrationForm.validation.mobileRequired') });
    } else {
        const phoneErr = getMobileValidationError(form.phone, t);
        if (phoneErr) {
            errors.push({ field: 'phone', message: phoneErr });
        }
    }

    return errors;
}

export function isVoterMigrationFormComplete(form, t) {
    return getVoterMigrationFormValidationErrors(form, t).length === 0;
}

/**
 * Every blank still empty, named the way the form asks for it and in the order
 * it asks — what the download confirmation lists back to the applicant.
 *
 * This covers all fields, not just the required ones: on Form-13 an empty
 * সনাক্তকারী block is as easy to forget as an empty name.
 *
 * @param {typeof INITIAL_VOTER_MIGRATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptyVoterMigrationFields(form, t) {
    return Object.keys(INITIAL_VOTER_MIGRATION_FORM)
        .filter((key) => !displayField(form?.[key]))
        .map((key) => t(`voterMigrationForm.fields.${key}`));
}

/** Everything the preview prints, already trimmed. */
export function buildVoterMigrationLetter(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_VOTER_MIGRATION_FORM)) {
        out[key] = displayField(form?.[key]);
    }
    return out;
}

/** @param {{ field: string, message: string }[]} errors */
export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) {
        map[field] = message;
    }
    return map;
}
